import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { saveAs } from 'file-saver';
import { finalize } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ContactAutocompleteResult } from 'src/app/services/contacts/contact-autocomplete.service';

type CargoSearchBy = 'date_range' | 'lading_uuid' | 'alternate_reference' | 'customer_po';

interface CargoSearchByOption {
  value: CargoSearchBy;
  label: string;
}

interface CargoReportRequestBody {
  customer_contact_uuid: string;
  search_by: CargoSearchBy;
  preview: boolean;
  date_from?: string;
  date_to?: string;
  search_values?: string[];
  resolved_ladings?: Record<string, string>;
}

interface CargoApiResponse {
  code?: number | string;
  message?: string;
  success?: boolean;
  error?: string;
  unresolved_ladings?: Record<string, CargoUnresolvedLading[]>;
}

interface CargoUnresolvedLading {
  lading_uuid: string;
  alternate_reference: string;
  customer_po: string;
  shipper_name: string;
  consignee_name: string;
  shipper_location: string;
  consignee_location: string;
  pickup_datetime: string;
  delivery_datetime: string;
}

interface CargoUnresolvedGroup {
  originalValue: string;
  ladings: CargoUnresolvedLading[];
}

@Component({
  selector: 'app-cargo',
  templateUrl: './cargo.component.html',
  styleUrl: './cargo.component.scss'
})
export class CargoComponent implements OnInit {
  readonly apiUrl = '/api/financials/cargo.php';
  readonly customerContactType = 'customer_contact_list';

  readonly searchByOptions: CargoSearchByOption[] = [
    { value: 'date_range', label: 'Date Range' },
    { value: 'lading_uuid', label: 'Lading UUID' },
    { value: 'alternate_reference', label: 'Alternate Reference' },
    { value: 'customer_po', label: 'Customer PO' },
  ];

  cargoForm: FormGroup = this.fb.group({
    customerContact: [null, Validators.required],
    search_by: ['date_range', Validators.required],
    preview: [true, Validators.required],
    date_from: [null, Validators.required],
    date_to: [null, Validators.required],
    raw_search_values: [''],
  });

  submitting = false;
  resolving = false;
  unresolvedGroups: CargoUnresolvedGroup[] = [];
  resolvedLadings: Record<string, string> = {};

  private lastRequestBody: CargoReportRequestBody | null = null;

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.searchByControl.valueChanges.subscribe(() => {
      this.applySearchModeValidators();
      this.clearResolutionState();
    });

    this.applySearchModeValidators();
  }

  get customerContactControl(): FormControl {
    return this.cargoForm.get('customerContact') as FormControl;
  }

  get searchByControl(): FormControl {
    return this.cargoForm.get('search_by') as FormControl;
  }

  get dateFromControl(): FormControl {
    return this.cargoForm.get('date_from') as FormControl;
  }

  get dateToControl(): FormControl {
    return this.cargoForm.get('date_to') as FormControl;
  }

  get rawSearchValuesControl(): FormControl {
    return this.cargoForm.get('raw_search_values') as FormControl;
  }

  get isDateRangeSearch(): boolean {
    return this.searchByControl.value === 'date_range';
  }

  get parsedSearchValues(): string[] {
    return this.parseSearchValues(this.rawSearchValuesControl.value);
  }

  get missingResolutionCount(): number {
    return this.unresolvedGroups
      .filter(group => group.ladings.length > 0)
      .filter(group => !this.resolvedLadings[group.originalValue])
      .length;
  }

  get unresolvableInputCount(): number {
    return this.unresolvedGroups.filter(group => group.ladings.length === 0).length;
  }

  get canSubmitResolvedLadings(): boolean {
    return !this.submitting
      && !this.resolving
      && this.unresolvedGroups.length > 0
      && this.missingResolutionCount === 0
      && this.unresolvableInputCount === 0;
  }

  onCustomerSelected(contact: ContactAutocompleteResult): void {
    this.customerContactControl.setValue(contact);
    this.clearResolutionState();
  }

  submitCargoReport(): void {
    const requestBody = this.buildRequestBody();
    console.log(requestBody);
    if (!requestBody) {
      return;
    }

    this.clearResolutionState();
    this.lastRequestBody = requestBody;
    this.postCargoRequest(requestBody, false);
  }

  submitResolvedLadings(): void {
    if (!this.lastRequestBody) {
      this.toastr.error('Original cargo report request is missing.');
      return;
    }

    if (!this.canSubmitResolvedLadings) {
      this.toastr.warning('Select one lading for each unresolved value.');
      return;
    }

    const requestBody: CargoReportRequestBody = {
      ...this.lastRequestBody,
      resolved_ladings: this.selectedResolvedLadings(),
    };

    this.postCargoRequest(requestBody, true);
  }

  resetForm(): void {
    this.cargoForm.reset({
      customerContact: null,
      search_by: 'date_range',
      preview: true,
      date_from: null,
      date_to: null,
      raw_search_values: '',
    });

    this.lastRequestBody = null;
    this.clearResolutionState();
    this.applySearchModeValidators();
  }

  selectResolvedLading(originalValue: string, ladingUuid: string): void {
    this.resolvedLadings = {
      ...this.resolvedLadings,
      [originalValue]: ladingUuid,
    };
  }

  selectedLadingFor(originalValue: string): string {
    return this.resolvedLadings[originalValue] || '';
  }

  controlInvalid(controlName: string): boolean {
    const control = this.cargoForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  trackUnresolvedGroup(_: number, group: CargoUnresolvedGroup): string {
    return group.originalValue;
  }

  trackLading(_: number, lading: CargoUnresolvedLading): string {
    return lading.lading_uuid;
  }

  private applySearchModeValidators(): void {
    if (this.isDateRangeSearch) {
      this.dateFromControl.setValidators(Validators.required);
      this.dateToControl.setValidators(Validators.required);
      this.rawSearchValuesControl.clearValidators();
      this.rawSearchValuesControl.setErrors(null);
    } else {
      this.dateFromControl.clearValidators();
      this.dateToControl.clearValidators();
      this.rawSearchValuesControl.setValidators(Validators.required);
    }

    this.dateFromControl.updateValueAndValidity({ emitEvent: false });
    this.dateToControl.updateValueAndValidity({ emitEvent: false });
    this.rawSearchValuesControl.updateValueAndValidity({ emitEvent: false });
  }

  private buildRequestBody(): CargoReportRequestBody | null {
    this.applySearchModeValidators();

    if (this.cargoForm.invalid) {
      this.cargoForm.markAllAsTouched();
      this.toastr.warning('Fill out the required cargo report fields.');
      return null;
    }

    const formValue = this.cargoForm.getRawValue();
    const customerContactUuid = this.contactUuidFromControl(formValue.customerContact);
    if (!customerContactUuid) {
      this.customerContactControl.setErrors({ required: true });
      this.toastr.warning('Select a customer contact.');
      return null;
    }

    const searchBy = formValue.search_by as CargoSearchBy;
    const requestBody: CargoReportRequestBody = {
      customer_contact_uuid: customerContactUuid,
      search_by: searchBy,
      preview: !!formValue.preview,
    };

    if (searchBy === 'date_range') {
      requestBody.date_from = this.formatDateForApi(formValue.date_from);
      requestBody.date_to = this.formatDateForApi(formValue.date_to);
      return requestBody;
    }

    const searchValues = this.parseSearchValues(formValue.raw_search_values);
    if (searchValues.length === 0) {
      this.rawSearchValuesControl.setErrors({ required: true });
      this.rawSearchValuesControl.markAsTouched();
      this.toastr.warning('Paste at least one search value.');
      return null;
    }

    requestBody.search_values = searchValues;
    return requestBody;
  }

  private postCargoRequest(requestBody: CargoReportRequestBody, isResolutionRequest: boolean): void {
    if (isResolutionRequest) {
      this.resolving = true;
    } else {
      this.submitting = true;
    }

    this.http.post(this.apiUrl, requestBody, {
      observe: 'response',
      responseType: 'blob',
    })
      .pipe(finalize(() => {
        this.submitting = false;
        this.resolving = false;
      }))
      .subscribe({
        next: response => this.handleBlobResponse(response, requestBody),
        error: error => this.handleHttpError(error),
      });
  }

  private handleBlobResponse(response: HttpResponse<Blob>, requestBody: CargoReportRequestBody): void {
    const blob = response.body;
    if (!blob) {
      this.toastr.error('Cargo report returned an empty response.');
      return;
    }

    this.parseBlobAsJson(blob, response.headers.get('content-type'))
      .then(jsonResponse => {
        if (jsonResponse) {
          this.handleJsonResponse(jsonResponse, requestBody);
          return;
        }

        const filename = this.filenameFromResponse(response) || this.defaultDownloadFilename();
        saveAs(blob, filename);
        this.clearResolutionState();
        this.toastr.success('Cargo report downloaded.');
      })
      .catch(() => {
        this.toastr.error('Could not read the cargo report response.');
      });
  }

  private handleJsonResponse(response: CargoApiResponse, requestBody: CargoReportRequestBody): void {
    if (Number(response.code) === 301) {
      this.unresolvedGroups = this.normalizeUnresolvedLadings(response.unresolved_ladings);
      this.resolvedLadings = {};
      this.lastRequestBody = requestBody;

      if (this.unresolvedGroups.length === 0) {
        this.toastr.warning(response.message || 'Some ladings need to be resolved, but no options were returned.');
        return;
      }

      this.toastr.warning(response.message || 'Select the matching lading for each unresolved value.');
      return;
    }

    const message = response.message || response.error || 'Cargo report request finished.';
    if (response.success === false || Number(response.code) >= 400) {
      this.toastr.error(message);
      return;
    }

    this.clearResolutionState();
    this.toastr.success(message);
  }

  private handleHttpError(error: HttpErrorResponse): void {
    if (error.error instanceof Blob) {
      this.parseBlobAsJson(error.error, error.headers?.get('content-type'))
        .then(jsonResponse => {
          if (jsonResponse && Number(jsonResponse.code) === 301 && this.lastRequestBody) {
            this.handleJsonResponse(jsonResponse, this.lastRequestBody);
            return;
          }

          const message = jsonResponse?.message || jsonResponse?.error || this.errorFallback(error);
          this.toastr.error(message);
        })
        .catch(() => this.toastr.error(this.errorFallback(error)));
      return;
    }

    this.toastr.error(error.error?.message || error.error?.error || this.errorFallback(error));
  }

  private async parseBlobAsJson(blob: Blob, contentType: string | null): Promise<CargoApiResponse | null> {
    const normalizedContentType = `${contentType || blob.type || ''}`.toLowerCase();
    const shouldTryParsing = normalizedContentType.includes('json')
      || normalizedContentType.includes('text')
      || blob.size <= 1024 * 1024;

    if (!shouldTryParsing) {
      return null;
    }

    const text = (await blob.text()).trim();
    if (!text || (!text.startsWith('{') && !text.startsWith('['))) {
      return null;
    }

    try {
      return JSON.parse(text) as CargoApiResponse;
    } catch {
      return null;
    }
  }

  private normalizeUnresolvedLadings(unresolved: CargoApiResponse['unresolved_ladings']): CargoUnresolvedGroup[] {
    if (!unresolved || typeof unresolved !== 'object' || Array.isArray(unresolved)) {
      return [];
    }

    return Object.entries(unresolved).map(([originalValue, ladings]) => ({
      originalValue,
      ladings: Array.isArray(ladings) ? ladings : [],
    }));
  }

  private selectedResolvedLadings(): Record<string, string> {
    return Object.fromEntries(
      Object.entries(this.resolvedLadings).filter(([, ladingUuid]) => !!ladingUuid)
    );
  }

  private parseSearchValues(value: unknown): string[] {
    const raw = value === null || value === undefined ? '' : `${value}`;
    const values = raw
      .split(/[\n\r\t,;]+/)
      .map(part => part.trim())
      .filter(Boolean);

    return Array.from(new Set(values));
  }

  private contactUuidFromControl(value: ContactAutocompleteResult | string | null): string {
    if (!value || typeof value === 'string') {
      return '';
    }

    return value.value || '';
  }

  private formatDateForApi(value: Date | string | null): string {
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return `${value}`;
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private filenameFromResponse(response: HttpResponse<Blob>): string {
    const contentDisposition = response.headers.get('content-disposition') || '';
    const utf8Filename = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)?.[1];
    if (utf8Filename) {
      return decodeURIComponent(utf8Filename.replace(/"/g, ''));
    }

    return /filename="?([^";]+)"?/i.exec(contentDisposition)?.[1] || '';
  }

  private defaultDownloadFilename(): string {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    return `cargo_report_${timestamp}.csv`;
  }

  private errorFallback(error: HttpErrorResponse): string {
    return `Cargo report request failed${error.status ? ` (${error.status})` : ''}.`;
  }

  private clearResolutionState(): void {
    this.unresolvedGroups = [];
    this.resolvedLadings = {};
  }
}
